import React, {createContext, useContext, useEffect, useMemo, useReducer} from 'react';
import {ActionType} from 'typesafe-actions';
import {useSelector} from 'react-redux';
import {AIMode, AssetStatus} from '@taopix/taopix-core';
import {UserAssetSelector} from '../../../Selectors';
import {AssetStatsActionTypes, AssetStatsActions} from './AssetStatsActions';
import {useExperience} from '../../Experience/ExperienceProvider';
import {AssetMetaInterface} from '@taopix/taopix-core/dist/types/types';

type RootAction = ActionType<typeof import('./AssetStatsActions')>;

type AssetStatsStateType = {
  totalPercentage: number;
  hiresPercentage: number;
  aiPercentage: number;
  processed: number[];
  aiProcessed: number[];
  total: number;
};

export type AssetStatsContextState = {
  state: AssetStatsStateType;
  dispatch: React.Dispatch<RootAction>;
  getNotReadyAssets: () => [number, AssetMetaInterface[]];
};

const AssetStatsContext = createContext<AssetStatsContextState>({} as AssetStatsContextState);

const AssetStatsReducer = (state: AssetStatsStateType, action: RootAction): AssetStatsStateType => {
  switch (action.type) {
    case AssetStatsActionTypes.SET_TOTAL: {
      return {
        ...state,
        total: action.payload,
      };
    }
    case AssetStatsActionTypes.SET_PROCESSED: {
      return {
        ...state,
        processed: Array.from(new Set([...action.payload])),
      };
    }
    case AssetStatsActionTypes.SET_AI_PROCESSED: {
      return {
        ...state,
        aiProcessed: Array.from(new Set([...action.payload])),
      };
    }
    case AssetStatsActionTypes.ADD_PROCESSED: {
      return {
        ...state,
        processed: Array.from(new Set([...state.processed].concat(action.payload))),
      };
    }
    case AssetStatsActionTypes.ADD_AI_PROCESSED: {
      return {
        ...state,
        aiProcessed: Array.from(new Set([...state.aiProcessed].concat(action.payload))),
      };
    }
    case AssetStatsActionTypes.SET_TOTAL_PERCENTAGE: {
      return {
        ...state,
        totalPercentage: action.payload,
      };
    }
    case AssetStatsActionTypes.SET_AI_PERCENTAGE: {
      return {
        ...state,
        aiPercentage: action.payload,
      };
    }
    case AssetStatsActionTypes.SET_HIRES_PERCENTAGE: {
      return {
        ...state,
        hiresPercentage: action.payload,
      };
    }
    default: {
      return state;
    }
  }
};

export const AssetStatsProvider = ({children}: {children: any}) => {
  const [state, dispatch] = useReducer(AssetStatsReducer, {
    totalPercentage: 0,
    hiresPercentage: 0,
    aiPercentage: 0,
    processed: [],
    aiProcessed: [],
    total: 0,
  });

  const assets = useSelector(UserAssetSelector);

  // get the AI mode from experience
  const {getAIMode} = useExperience();

  useEffect(() => {
    const aiMode = getAIMode();

    // set the total
    dispatch(AssetStatsActions.setTotal(assets.length || 0));

    // loop through the assets and check their status
    const result = assets.reduce(
      (acc: {processed: number[]; aiProcessed: number[]}, asset) => {
        if (
          [
            AssetStatus.Processed,
            AssetStatus.UseFromHostsServer,
            AssetStatus.Error,
            AssetStatus.Enhancing,
          ].includes(asset.status)
        ) {
          acc.processed.push(asset.id);
        }

        if (aiMode > AIMode.NotAvailable) {
          // if the hires has an error, set the ai file to processed
          if (AssetStatus.Error === asset.status) {
            acc.aiProcessed.push(asset.id);
          } else if ([AssetStatus.Processed, AssetStatus.Error].includes(asset?.metaData?.status)) {
            acc.aiProcessed.push(asset.id);
          }
        }

        return acc;
      },
      {processed: [], aiProcessed: []}
    );

    dispatch(AssetStatsActions.addProcessed(result.processed));
    dispatch(AssetStatsActions.addAIProcessed(result.aiProcessed));
  }, [assets]);

  useEffect(() => {
    const aiMode = getAIMode();

    // if the total number of assets is 0, reset
    if (0 === state.total) {
      if (0 !== state.processed.length) {
        dispatch(AssetStatsActions.setProcessed([]));
      }

      if (0 !== state.aiProcessed.length) {
        dispatch(AssetStatsActions.setAIProcessed([]));
      }

      if (0 !== state.totalPercentage) {
        dispatch(AssetStatsActions.setTotalPercentage(0));
      }

      if (0 !== state.aiPercentage) {
        dispatch(AssetStatsActions.setAIPercentage(0));
      }

      if (0 !== state.hiresPercentage) {
        dispatch(AssetStatsActions.setHiresPercentage(0));
      }

      return;
    }

    const hires = Math.round((state.processed.length / state.total) * 100);
    dispatch(AssetStatsActions.setHiresPercentage(hires));

    if (aiMode > AIMode.NotAvailable) {
      const ai = Math.round((state.aiProcessed.length / state.total) * 100);
      dispatch(AssetStatsActions.setAIPercentage(ai));
      if (ai > 0) {
        dispatch(AssetStatsActions.setTotalPercentage(Math.round((ai + hires) / 2)));
      } else {
        dispatch(AssetStatsActions.setTotalPercentage(hires));
      }
    } else {
      dispatch(AssetStatsActions.setTotalPercentage(hires));
    }
  }, [
    state.processed,
    state.aiProcessed,
    state.aiPercentage,
    state.hiresPercentage,
    state.total,
    state.totalPercentage,
  ]);

  useEffect(() => {
    const aiMode = getAIMode();

    const token = PubSub.subscribe('ASSET_STATS_DELETION', (msg: string, payload: number) => {
      dispatch(AssetStatsActions.setProcessed([...state.processed.filter(id => id !== payload)]));

      if (aiMode > AIMode.NotAvailable) {
        dispatch(
          AssetStatsActions.setAIProcessed([...state.aiProcessed.filter(id => id !== payload)])
        );
      }
    });

    return () => {
      PubSub.unsubscribe(token);
    };
  }, [state.processed, state.aiProcessed]);

  /**
   * Returns a list of assets that are not ready to use.
   *
   * @returns List of not ready assets and the number.
   */
  const getNotReadyAssets = (): [number, AssetMetaInterface[]] => {
    if (0 === assets.length) return [-1, [] as AssetMetaInterface[]];
    const notReadyAssets = assets.filter(asset => {
      // If asset is not queued and there is no uploadId, then it's a failed uploaded
      // usually when the page is reloaded before the asset is uploaded to the image server.
      if (!asset.hasOwnProperty('uploadId') && AssetStatus.NotQueued === asset.status) {
        return false;
      }

      // Ignore certain statuses.
      if ([AssetStatus.Error].includes(asset.status)) {
        return false;
      }

      // External assets needs to wait for the status to change as they will have a thumbnail immediately.
      if (asset.hasOwnProperty('externalId') && '' !== asset.externalId) {
        return ![AssetStatus.Processed].includes(asset.status);
      }

      return (
        // heic images are missing thumbnailsrc until they are processed so need to check it exists.
        !asset.hasOwnProperty('thumbnailsrc') ||
        '' === asset.thumbnailsrc ||
        undefined === asset.thumbnailsrc
      );
    });

    return [notReadyAssets.length, notReadyAssets];
  };

  const contextValue = useMemo(
    () => ({
      state,
      dispatch,
      getNotReadyAssets,
    }),
    [state, dispatch]
  );

  return <AssetStatsContext.Provider value={contextValue}>{children}</AssetStatsContext.Provider>;
};

export const useAssetStats = () => {
  const context = useContext(AssetStatsContext);
  if (context === undefined) {
    throw new Error('useAssetStats must be used within an AssetStatsProvider');
  }
  return context;
};
